import {
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnFiltersState,
  getSortedRowModel,
  PaginationState,
  getCoreRowModel,
  useReactTable,
  SortingState,
} from '@ui/components';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { getOnboardingImportEmployees } from '@api/onboarding';
import { EmployeeTableDataDto } from '@interfaces/models';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@ui/hooks';

import { getImportEmployeesColumns } from './import-employees-columns';

export const useImportEmployeesTable = ({
  onEditEmployee,
  onRemoveEmployee,
}: {
  onRemoveEmployee: (employee: EmployeeTableDataDto) => void;
  onEditEmployee: (employee: EmployeeTableDataDto) => void;
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Use a ref to store the last known totalItems value.
  // This is used to avoid flickering when the data is loading.
  // I do not want to use placeholderData in useQuery because loader is not visible then when changing pagination.
  const totalItemsRef = useRef<number>(0);

  const columns = getImportEmployeesColumns(t, onEditEmployee, onRemoveEmployee);

  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const debouncedTableSearchQuery = useDebounce(tableSearchQuery, 800);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data: apiData, isLoading } = useQuery({
    queryKey: [
      'employees-table',
      {
        pagination,
        sorting,
        search: debouncedTableSearchQuery.length >= 2 ? debouncedTableSearchQuery : undefined,
      },
    ],
    queryFn: () =>
      getOnboardingImportEmployees({
        pagination: {
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        },
        sort: sorting.length
          ? {
              direction: sorting[0]?.desc ? 'DESC' : 'ASC',
              columnName: sorting[0]?.id ?? '',
            }
          : undefined,
        filters: {
          search: debouncedTableSearchQuery.length >= 2 ? debouncedTableSearchQuery : undefined,
        },
      }),
  });

  const refetchTable = () => {
    queryClient.invalidateQueries({ queryKey: ['employees-table'] });
  };

  // Update the ref whenever we get valid data
  useEffect(() => {
    if (apiData?.pagination?.totalItems !== undefined) {
      totalItemsRef.current = apiData.pagination.totalItems;
    }
  }, [apiData?.pagination?.totalItems]);

  // Get the current totalItems - prioritize actual data if available. Needed on first render.
  const totalItems =
    apiData?.pagination?.totalItems !== undefined
      ? apiData.pagination.totalItems
      : totalItemsRef.current;

  const table = useReactTable({
    data: apiData?.data ?? [],
    columns: columns,
    state: { pagination, sorting, globalFilter: tableSearchQuery, columnFilters, rowSelection },
    pageCount: Math.ceil(totalItems / pagination.pageSize),
    rowCount: totalItems,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    getRowId: (row) => row.id as unknown as string,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setTableSearchQuery,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  return {
    globalFilter: table.getState().globalFilter,
    columnsLength: columns.length,
    data: apiData,
    refetchTable,
    totalItems,
    isLoading,
    table,
  };
};
